/**
 * Button Variants
 *
 * CVA variants for the Button component.
 * Exported separately to comply with React Fast Refresh requirements.
 *
 * @see Button.tsx for component implementation
 */

import {cva} from 'class-variance-authority';

/**
 * Button style variants using class-variance-authority.
 *
 * Base styles include:
 * - Flexbox centering
 * - Design system border radius
 * - Focus ring from tokens (--ring)
 * - Smooth transitions
 * - Disabled state styling
 * - Mobile touch targets (min 44px)
 */
export const buttonVariants = cva(
  // Base styles
  [
    // Layout & positioning
    'inline-flex items-center justify-center gap-2',
    // Typography
    'font-medium whitespace-nowrap',
    // Border radius from tokens
    'rounded-lg',
    // Transitions
    'transition-colors duration-200 ease-out',
    // Focus ring from design tokens
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-ring',
    'focus-visible:ring-offset-2',
    'focus-visible:ring-offset-background',
    // Disabled state
    'disabled:pointer-events-none',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    // Mobile touch target (44px minimum)
    'min-h-touch sm:min-h-0',
    // Touch optimization
    'touch-manipulation',
    // Cursor
    'cursor-pointer',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary: Main CTAs, brand color
        primary: [
          'bg-primary text-primary-foreground',
          'hover:bg-primary-hover',
          'active:bg-primary-active',
          'shadow-sm hover:shadow-md',
        ].join(' '),

        // Secondary: Less emphasis
        secondary: [
          'bg-secondary text-secondary-foreground',
          'hover:bg-secondary-hover',
          'active:bg-secondary-active',
          'shadow-sm',
        ].join(' '),

        // Outline: Bordered, transparent background
        outline: [
          'border border-border bg-transparent',
          'text-foreground',
          'hover:bg-muted hover:text-foreground',
          'active:bg-muted/80',
        ].join(' '),

        // Ghost: Minimal styling, transparent bg
        ghost: [
          'bg-transparent text-foreground',
          'hover:bg-muted hover:text-foreground',
          'active:bg-muted/80',
        ].join(' '),

        // Ghost Success: Ghost variant with success colors
        'ghost-success': [
          'bg-transparent text-emerald-600',
          'hover:bg-emerald-50 hover:text-emerald-700',
          'active:bg-emerald-50 active:text-emerald-700',
        ].join(' '),

        // Ghost Destructive: Ghost variant with destructive colors
        'ghost-destructive': [
          'bg-transparent text-rose-600',
          'hover:bg-rose-50 hover:text-rose-700',
          'active:bg-rose-50 active:text-rose-700',
        ].join(' '),

        // Destructive: Dangerous actions
        destructive: [
          'bg-destructive text-destructive-foreground',
          'hover:bg-destructive-hover',
          'shadow-sm hover:shadow-md',
        ].join(' '),

        // Success: Confirmation, approve actions
        success: [
          'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]',
          'hover:bg-[hsl(var(--success)/0.9)]',
          'active:bg-[hsl(var(--success)/0.85)]',
          'shadow-sm hover:shadow-md',
        ].join(' '),

        // Link: Text-only, underline on hover
        link: [
          'bg-transparent text-primary',
          'underline-offset-4 hover:underline',
          'h-auto min-h-0 p-0',
          'shadow-none',
        ].join(' '),

        // Legacy: default (same as primary)
        default: [
          'bg-primary text-primary-foreground',
          'hover:bg-primary-hover',
          'active:bg-primary-active',
          'shadow-sm hover:shadow-md',
        ].join(' '),

        // Legacy: tangi (brand gradient)
        tangi: [
          'btn-tangi-primary text-primary-foreground',
          'shadow-md hover:shadow-lg',
        ].join(' '),

        // Legacy: premium (gradient effect)
        premium: [
          'bg-gradient-to-r from-primary via-accent to-primary',
          'bg-[length:200%_100%] bg-[position:0%_50%]',
          'hover:bg-[position:100%_50%]',
          'text-primary-foreground',
          'shadow-lg hover:shadow-xl',
          'transition-all duration-1000 ease-out',
        ].join(' '),

        // Glass: Liquid glass morphism effect
        glass: [
          'fluid-button',
          'text-foreground',
          'hover:transform hover:translate-y-[-1px] hover:scale-[1.02]',
          'active:scale-[0.98]',
        ].join(' '),
      },

      size: {
        sm: ['h-8 sm:h-8 px-3 py-1.5', 'text-sm'].join(' '),
        md: ['h-10 sm:h-10 px-4 py-2', 'text-sm'].join(' '),
        lg: ['h-12 sm:h-12 px-6 py-3', 'text-base font-semibold'].join(' '),
        icon: ['h-10 !w-10 p-0', 'rounded-lg'].join(' '),
        default: ['h-10 sm:h-10 px-4 py-2', 'text-sm'].join(' '),
        xl: ['h-14 sm:h-14 px-8 py-3.5', 'text-lg font-semibold'].join(' '),
        // Caption: Small text size for compact buttons
        caption: ['h-8 sm:h-8 px-3 py-1.5', 'text-caption'].join(' '),
      },

      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },

      isLoading: {
        true: 'cursor-wait',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
      isLoading: false,
    },
  }
);
